/**
 * wishlistEngine.js — Rê Leve
 *
 * Coleta e analisa desejos dos clientes (assinantes + pacotes mensais):
 *   - Ingredientes que gostariam de ver no cardápio
 *   - Pratos que gostariam de pedir
 *   - Comentários abertos
 *
 * Cruza com a base nutricional ampla para sugerir RECEITAS PRONTAS que
 * combinam os ingredientes mais desejados, mantendo a pegada saudável
 * e exigente da Rê Leve.
 */

import { NUTRITION_DB, findFood } from './nutritionDatabase';

const LS_KEY = 'releve.wishlist';

// Seeds: 12 desejos plausíveis de clientes para o painel já nascer cheio
const SEED = [
  { id: 'w1', customerId: 'sim-fiel-1',  customerName: 'Camila V.',     type: 'ingrediente', value: 'mandioquinha',       priority: 'alta',  at: ago(2), notes: 'gosta de mandioquinha como acompanhamento de proteína magra' },
  { id: 'w2', customerId: 'sim-fiel-2',  customerName: 'Marcelo R.',    type: 'ingrediente', value: 'salmão',             priority: 'alta',  at: ago(3), notes: '' },
  { id: 'w3', customerId: 'sim-fiel-3',  customerName: 'Beatriz L.',    type: 'ingrediente', value: 'quinoa',             priority: 'média', at: ago(5), notes: 'low carb mas com saciedade' },
  { id: 'w4', customerId: 'sim-alto-1',  customerName: 'Renata S.',     type: 'prato',       value: 'curry de grão-de-bico', priority: 'alta', at: ago(1), notes: 'vegano, sem ovo' },
  { id: 'w5', customerId: 'sim-fiel-1',  customerName: 'Camila V.',     type: 'ingrediente', value: 'batata-doce',        priority: 'média', at: ago(7), notes: '' },
  { id: 'w6', customerId: 'sim-rec-1',   customerName: 'Pedro A.',      type: 'ingrediente', value: 'salmão',             priority: 'média', at: ago(4), notes: 'pra dia de pós-treino' },
  { id: 'w7', customerId: 'sim-rec-2',   customerName: 'Joana M.',      type: 'prato',       value: 'escondidinho de frango com mandioquinha', priority: 'alta', at: ago(1), notes: 'comida regional saudável' },
  { id: 'w8', customerId: 'sim-novo-1',  customerName: 'Lucas T.',      type: 'ingrediente', value: 'tofu',               priority: 'baixa', at: ago(10), notes: '' },
  { id: 'w9', customerId: 'sim-fiel-4',  customerName: 'Daniela F.',    type: 'ingrediente', value: 'mandioquinha',       priority: 'alta',  at: ago(6), notes: 'amo mandioquinha gratinada' },
  { id: 'w10',customerId: 'sim-alto-2',  customerName: 'Fernanda K.',   type: 'prato',       value: 'risoto integral de cogumelos', priority: 'média', at: ago(2), notes: 'vegetariano' },
  { id: 'w11',customerId: 'sim-fiel-5',  customerName: 'Henrique B.',   type: 'ingrediente', value: 'abacate',            priority: 'média', at: ago(8), notes: 'café da manhã ou almoço' },
  { id: 'w12',customerId: 'sim-rec-3',   customerName: 'Aline C.',      type: 'ingrediente', value: 'salmão',             priority: 'alta',  at: ago(1), notes: 'saudável sem fritura' },
];

function ago(daysBack) {
  const d = new Date();
  d.setDate(d.getDate() - daysBack);
  return d.toISOString();
}

function load() {
  if (typeof window === 'undefined') return SEED;
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) { localStorage.setItem(LS_KEY, JSON.stringify(SEED)); return SEED; }
    return JSON.parse(raw);
  } catch { return SEED; }
}
function save(list) {
  if (typeof window === 'undefined') return;
  try { localStorage.setItem(LS_KEY, JSON.stringify(list)); } catch {}
}

// ── API pública ───────────────────────────────────────────────────────────
export function getWishlist() { return load(); }

export function addWish(w) {
  const list = load();
  const newW = { id: 'w-' + Date.now(), at: new Date().toISOString(), priority: 'média', ...w };
  save([newW, ...list]);
  return newW;
}

export function removeWish(id) {
  save(load().filter(w => w.id !== id));
}

// Top ingredientes pedidos (com contagem)
export function getTopIngredients({ limit = 8 } = {}) {
  const wishes = load().filter(w => w.type === 'ingrediente');
  const map = {};
  wishes.forEach(w => {
    const k = (w.value || '').toLowerCase().trim();
    if (!k) return;
    if (!map[k]) {
      map[k] = { name: w.value, count: 0, customers: new Set(), highPriority: 0 };
    }
    map[k].count += 1;
    map[k].customers.add(w.customerId);
    if (w.priority === 'alta') map[k].highPriority += 1;
  });

  return Object.values(map)
    .map(x => ({
      name: x.name,
      count: x.count,
      uniqueCustomers: x.customers.size,
      highPriorityShare: x.highPriority / x.count,
      nutrition: findFood(x.name),
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

export function getTopDishes({ limit = 5 } = {}) {
  const wishes = load().filter(w => w.type === 'prato');
  const map = {};
  wishes.forEach(w => {
    const k = (w.value || '').toLowerCase().trim();
    if (!k) return;
    if (!map[k]) {
      map[k] = { name: w.value, count: 0, customers: new Set(), notes: [] };
    }
    map[k].count += 1;
    map[k].customers.add(w.customerId);
    if (w.notes) map[k].notes.push(w.notes);
  });
  return Object.values(map)
    .map(x => ({ name: x.name, count: x.count, uniqueCustomers: x.customers.size, notes: x.notes }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

// ── Sugestões de receita ───────────────────────────────────────────────────
// Combina top ingredientes em receitas com perfil saudável + recifense,
// mantendo a pegada exigente da Rê Leve.

const RECIPE_TEMPLATES = [
  {
    name: 'Escondidinho de Mandioquinha com Frango Caipira',
    base: ['mandioquinha', 'frango'],
    boost: ['azeite', 'cebola', 'salsinha'],
    description: 'Comida regional pernambucana releitura saudável. Mandioquinha cremosa, frango grelhado desfiado, ervas frescas.',
    profile: { kcal: 420, protein: 32, carb: 38, fat: 14 },
    healthScore: 9,
    tags: ['regional', 'leve', 'sem-glúten'],
  },
  {
    name: 'Salmão Grelhado com Quinoa e Mix da Estação',
    base: ['salmão', 'quinoa'],
    boost: ['azeite', 'limão', 'rúcula', 'tomate-cereja'],
    description: 'Filé de salmão grelhado, quinoa cozida em caldo de legumes, mix de folhas. Alto em ômega-3 e proteína de qualidade.',
    profile: { kcal: 490, protein: 36, carb: 32, fat: 22 },
    healthScore: 10,
    tags: ['pós-treino', 'low-carb-flex', 'sem-glúten'],
  },
  {
    name: 'Curry Cremoso de Grão-de-Bico (100% vegano)',
    base: ['grão-de-bico', 'leite-de-coco'],
    boost: ['cebola', 'cúrcuma', 'gengibre', 'coentro', 'arroz-integral'],
    description: 'Curry suave com leite de coco light, cúrcuma e gengibre. Servido com arroz integral. Sem ingredientes de origem animal.',
    profile: { kcal: 470, protein: 18, carb: 60, fat: 16 },
    healthScore: 9,
    tags: ['vegano', 'sem-lactose', 'comida-de-conforto'],
  },
  {
    name: 'Risoto Integral de Cogumelos',
    base: ['cogumelos', 'arroz-integral'],
    boost: ['cebola', 'alho-poró', 'parmesão', 'azeite'],
    description: 'Arroz integral cremoso com mix de cogumelos paris+shiitake. Sem creme de leite — cremosidade vem do amido natural.',
    profile: { kcal: 440, protein: 14, carb: 64, fat: 12 },
    healthScore: 8,
    tags: ['vegetariano', 'comida-de-conforto'],
  },
  {
    name: 'Frango ao Curry com Batata-Doce Assada',
    base: ['frango', 'batata-doce'],
    boost: ['cúrcuma', 'iogurte-natural', 'cebola', 'salsinha'],
    description: 'Frango em molho de curry suave com batata-doce assada em cubos. Alta carga proteica, sabor marcante.',
    profile: { kcal: 460, protein: 38, carb: 42, fat: 12 },
    healthScore: 9,
    tags: ['pós-treino', 'sem-glúten'],
  },
  {
    name: 'Bowl de Abacate, Quinoa e Frango',
    base: ['abacate', 'quinoa', 'frango'],
    boost: ['limão', 'rúcula', 'gergelim', 'azeite'],
    description: 'Bowl frio: quinoa, frango grelhado em tiras, abacate, rúcula, molho cítrico. Energia de longo prazo + gordura boa.',
    profile: { kcal: 510, protein: 32, carb: 38, fat: 24 },
    healthScore: 9,
    tags: ['low-carb-flex', 'sem-glúten', 'almoço-ou-jantar'],
  },
  {
    name: 'Mandioquinha Gratinada com Frango ao Forno',
    base: ['mandioquinha', 'frango'],
    boost: ['queijo-light', 'tomilho', 'alho'],
    description: 'Mandioquinha em rodelas gratinada com queijo light, frango temperado com tomilho e alho. Sabor de domingo, perfil leve.',
    profile: { kcal: 480, protein: 34, carb: 44, fat: 18 },
    healthScore: 8,
    tags: ['regional', 'comida-de-conforto'],
  },
  {
    name: 'Tofu Grelhado Asiático com Brócolis e Arroz',
    base: ['tofu', 'brócolis'],
    boost: ['gengibre', 'shoyu-low-sodio', 'gergelim', 'arroz-integral'],
    description: 'Tofu marinado em shoyu+gengibre, grelhado, com brócolis no vapor e arroz integral. Proteína vegetal completa.',
    profile: { kcal: 420, protein: 24, carb: 50, fat: 14 },
    healthScore: 9,
    tags: ['vegano', 'sem-lactose'],
  },
];

export function suggestRecipes({ limit = 4 } = {}) {
  const top = getTopIngredients({ limit: 10 }).map(t => t.name.toLowerCase());

  const scored = RECIPE_TEMPLATES.map(r => {
    const baseHits = r.base.filter(b => top.some(t => t.includes(b.toLowerCase()))).length;
    const boostHits = r.boost.filter(b => top.some(t => t.includes(b.toLowerCase()))).length;
    const score = baseHits * 3 + boostHits + r.healthScore * 0.2;
    return { ...r, score, baseHits, boostHits };
  });

  return scored
    .filter(s => s.baseHits > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}
